// Element identification: turn a DOM node into identifiers a developer (or an agent
// grepping the codebase) can map back to source. Strongest signal first:
//   1. explicit identifier attributes on the element and its ancestors, as a tree
//   2. framework component chain (React fiber / Vue instance)
//   3. a short CSS selector and visible text as a fallback

export type Target = {
  /** Human-readable location, e.g. "checkout › cart › pay-button". */
  label: string;
  /** Identifier attributes from outermost to innermost ancestor. */
  tree: { attr: string; value: string }[];
  /** Framework components from outermost to innermost, when detectable. */
  components?: string[];
  /** Source location when the framework exposes it (React <=18 dev builds). */
  source?: string;
  selector: string;
  tag: string;
  text?: string;
  role?: string;
  ariaLabel?: string;
  rect: { x: number; y: number; w: number; h: number };
};

export const DEFAULT_ATTRIBUTES = ["data-feedback-id", "data-testid", "data-test", "data-cy", "id"];

/** Generated ids (React useId, numbered ids, hashes) are noise, not identifiers. */
const GENERATED_ID = /^(radix-|base-ui-|headlessui-|react-aria|mui-|rc-|chakra-|downshift-|mantine-|ember\d|__next)|_r[0-9a-z]+_|^r[0-9a-z]{1,4}$/i;

function isStableId(value: string): boolean {
  return !!value && value.length <= 80 && !/^:|[:«»]|\d{3,}|[0-9a-f]{8,}/i.test(value) && !GENERATED_ID.test(value);
}

function identifierOf(el: Element, attributes: string[]): { attr: string; value: string } | null {
  for (const attr of attributes) {
    const value = el.getAttribute(attr);
    if (value && (attr !== "id" || isStableId(value))) return { attr, value };
  }
  return null;
}

function parentOf(el: Element): Element | null {
  return el.parentElement ?? ((el.getRootNode() as ShadowRoot).host || null);
}

export function identifierTree(el: Element, attributes: string[]): { attr: string; value: string }[] {
  const tree: { attr: string; value: string }[] = [];
  for (let cur: Element | null = el; cur && cur !== document.body; cur = parentOf(cur)) {
    const id = identifierOf(cur, attributes);
    if (id) tree.unshift(id);
  }
  return tree;
}

export function cssSelector(el: Element): string {
  const parts: string[] = [];
  for (let cur: Element | null = el; cur && cur !== document.body && parts.length < 5; cur = cur.parentElement) {
    if (cur.id && isStableId(cur.id)) {
      parts.unshift(`#${CSS.escape(cur.id)}`);
      break;
    }
    let part = cur.tagName.toLowerCase();
    const cls = [...cur.classList].filter((c) => /^[a-z][\w-]{1,30}$/i.test(c) && !/\d{3,}/.test(c)).slice(0, 2);
    if (cls.length) part += "." + cls.map((c) => CSS.escape(c)).join(".");
    const parent = cur.parentElement;
    if (parent) {
      const same = [...parent.children].filter((s) => s.tagName === cur!.tagName);
      if (same.length > 1) part += `:nth-of-type(${same.indexOf(cur) + 1})`;
    }
    parts.unshift(part);
  }
  return parts.join(" > ");
}

// ---------------------------------------------------------------------------
// Framework component detection
// ---------------------------------------------------------------------------

type Fiber = { tag: number; type: any; return: Fiber | null; _debugSource?: { fileName: string; lineNumber: number } };

const REACT_COMPONENT_TAGS = new Set([0, 1, 11, 14, 15]); // function, class, forwardRef, memo, simple memo
const SKIP = /^(Anonymous|Fragment|Suspense|StrictMode|Provider|Consumer|Context|Router|Routes|Route|Outlet|Link|ErrorBoundary|Hydrat|Inner|Server|Client|Layout(Router|Segment)|Redirect|NotFound|Loading|Scroll|Head|Hot|AppRouter|Template|RenderFromTemplate)/;

function componentName(type: any): string | null {
  if (!type || typeof type === "string") return null;
  const name = type.displayName || type.name || type.render?.displayName || type.render?.name || type.type?.displayName || type.type?.name;
  // Minified production names ("a", "Xe") tell nobody anything.
  return typeof name === "string" && name.length > 2 && !SKIP.test(name) ? name : null;
}

function reactInfo(el: Element): { components: string[]; source?: string } | null {
  const key = Object.keys(el).find((k) => k.startsWith("__reactFiber$") || k.startsWith("__reactInternalInstance$"));
  if (!key) return null;
  const components: string[] = [];
  let source: string | undefined;
  for (let f: Fiber | null = (el as any)[key]; f && components.length < 8; f = f.return) {
    if (!source && f._debugSource) source = `${f._debugSource.fileName}:${f._debugSource.lineNumber}`;
    if (!REACT_COMPONENT_TAGS.has(f.tag)) continue;
    const name = componentName(f.type);
    if (name && components[0] !== name) components.unshift(name);
  }
  return { components, source };
}

function vueInfo(el: Element): { components: string[]; source?: string } | null {
  let node: Element | null = el;
  while (node && !(node as any).__vueParentComponent) node = node.parentElement;
  let inst = node && (node as any).__vueParentComponent;
  if (!inst) return null;
  const components: string[] = [];
  let source: string | undefined = inst.type?.__file;
  for (; inst && components.length < 8; inst = inst.parent) {
    const name = inst.type?.name || inst.type?.__name;
    if (name && name.length > 2) components.unshift(name);
  }
  return { components, source };
}

// ---------------------------------------------------------------------------

export function describe(el: Element, attributes: string[]): Target {
  const tree = identifierTree(el, attributes);
  const fw = reactInfo(el) ?? vueInfo(el);
  const r = el.getBoundingClientRect();
  const text = ((el as HTMLElement).innerText ?? el.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, 120) || undefined;
  const tag = el.tagName.toLowerCase();
  const ownId = identifierOf(el, attributes);

  // Label: the identifier tree, plus the clicked element itself when it has no identifier.
  let label = tree.map((t) => t.value).join(" › ");
  if (!ownId) {
    const leaf = text && text.length <= 40 ? `${tag} "${text}"` : tag;
    label = label ? `${label} › ${leaf}` : fw?.components.length ? `${fw.components[fw.components.length - 1]} › ${leaf}` : cssSelector(el);
  }

  return {
    label,
    tree,
    components: fw?.components.length ? fw.components : undefined,
    source: fw?.source,
    selector: cssSelector(el),
    tag,
    text,
    role: el.getAttribute("role") ?? undefined,
    ariaLabel: el.getAttribute("aria-label") ?? undefined,
    rect: { x: Math.round(r.left + scrollX), y: Math.round(r.top + scrollY), w: Math.round(r.width), h: Math.round(r.height) },
  };
}

/** The identified section at the centre of the viewport: what the tester is looking at. */
export function sectionInView(attributes: string[], ignore: Element): string | null {
  const el = document.elementFromPoint(innerWidth / 2, innerHeight / 2);
  if (!el || ignore.contains(el)) return null;
  const tree = identifierTree(el, attributes);
  return tree.length ? tree.map((t) => t.value).join(" › ") : null;
}

export function currentRoute(): string {
  return location.pathname + (location.hash.startsWith("#/") ? location.hash : "");
}
